/*
 * Homepage "latest" slider - a coverflow carousel that sits directly under
 * the nav, above the hero banner.
 *
 * Slides are mostly live content, so the slider keeps itself current with no
 * deploy: the most recently updated listings (from the properties Home.js has
 * already fetched) and a blog post - FEATURED_POST_SLUG if set, otherwise the
 * newest. Between them sit a few fixed slides for the pages that should
 * always be one click from the homepage - the instant valuation and the
 * guides. Edit FIXED_SLIDES to change those, and EXTRAS_ORDER to reorder them.
 *
 * Swiper 6's loop mode clones slides once at init and does not cope with the
 * slide list changing afterwards, so the slides only mount once the posts
 * request has settled (successfully or not). Until then a placeholder holds
 * the space.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { A11y, Autoplay, EffectCoverflow, Keyboard, Navigation, Pagination } from "swiper/core";
import "swiper/swiper.min.css";
import "swiper/components/effect-coverflow/effect-coverflow.min.css";
import "swiper/components/pagination/pagination.min.css";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";

import { Container } from "../styles/Layout";
import { size } from "../styles/Breakpoints";
import { propertyPath } from "../../data/propertySlug";
import { postPath, findPostBySlug } from "../../data/postSlug";
import { extractYouTubeId, youtubeThumbnail } from "../../data/youtubeVideo";

SwiperCore.use([A11y, Autoplay, EffectCoverflow, Keyboard, Navigation, Pagination]);

const LISTING_COUNT = 3;

const FIXED_SLIDES = [
    {
        key: "valuation",
        tag: "Free tool",
        title: "What's your home worth?",
        subtitle: "Get an instant online valuation in under a minute",
        cta: "Get a valuation",
        image: "/images/guides/shopfront.jpg",
        href: "https://www.onthemarket.com/instant-valuation/property-people/",
    },
    {
        key: "sales-guide",
        tag: "Sellers",
        title: "The 2026 sales guide",
        subtitle: "Every step from valuation to completion",
        cta: "Read the guide",
        image: "/images/guides/sales-hero.jpg",
        to: "/sales-guide",
    },
    {
        key: "lettings-guide",
        tag: "Landlords",
        title: "The 2026 lettings guide",
        subtitle: "Fees, compliance and the Renters' Rights Act, explained",
        cta: "Read the guide",
        // Not letting-hero.jpg: the management slide already uses it.
        image: "/images/guides/interior-1.jpg",
        to: "/lettings-guide",
    },
    {
        key: "management-guide",
        tag: "Landlords",
        title: "360 Management, explained",
        subtitle: "Our fully managed lettings service",
        cta: "Read the guide",
        image: "/images/guides/letting-hero.jpg",
        to: "/management-guide",
    },
];

/*
 * The order the non-listing slides appear in, by key. BLOG_POST marks where
 * the blog post goes; it is skipped if there isn't one.
 */
const BLOG_POST = "blog-post";
const EXTRAS_ORDER = ["valuation", "lettings-guide", "sales-guide", BLOG_POST, "management-guide"];

/*
 * The blog post to feature, as its title slug - the readable part of its
 * /blog/<slug>-<id> URL. Matched loosely against post titles (see
 * findPostBySlug), so punctuation and case don't matter. If no published post
 * matches - renamed, unpublished, or this is set to "" - the newest post with
 * an image is used instead.
 */
const FEATURED_POST_SLUG = "";

function formatPrice(property) {
    const amount = "£" + String(property.price).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return property.category === "Lettings" ? `${amount} ${property.priceType || "PCM"}` : amount;
}

function listingTag(property) {
    if (property.status === "Under Offer") return "Under offer";
    return property.category === "Lettings" ? "To let" : "For sale";
}

function listingSlide(property) {
    const beds = property.bedrooms ? `${property.bedrooms} bed ` : "";
    return {
        key: property._id,
        isListing: true,
        tag: listingTag(property),
        title: `${beds}${(property.type || "property").toLowerCase()}, ${property.displayAddress}`,
        subtitle: `${property.location} · ${formatPrice(property)}`,
        cta: "View property",
        image: property.photos[0],
        to: propertyPath(property),
    };
}

function postSlide(post) {
    const videoId = post.videoUrl && extractYouTubeId(post.videoUrl);
    return {
        key: post._id,
        tag: "From the blog",
        title: post.title,
        subtitle: post.excerpt,
        cta: "Read article",
        image: post.image || (videoId && youtubeThumbnail(videoId)),
        to: postPath(post),
    };
}

/*
 * Listings and extras (fixed slides plus the blog post, in EXTRAS_ORDER)
 * alternate. There are more extras than listings, so the last extras run
 * back to back.
 */
function buildSlides(properties, posts) {
    /*
     * By updatedAt, the same order as the "Recently Added Properties" list
     * further down the page (the /properties/available?page= endpoint), so the
     * two agree. A listing that is edited or put back on the market moves to
     * the front.
     */
    const listings = [...properties]
        .filter((p) => p.photos && p.photos.length)
        .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))
        .slice(0, LISTING_COUNT)
        .map(listingSlide);

    const postsWithImages = posts.filter((p) => p.image || p.videoUrl);
    const blogPost =
        (FEATURED_POST_SLUG && findPostBySlug(FEATURED_POST_SLUG, postsWithImages)) ||
        [...postsWithImages].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))[0];

    const extras = EXTRAS_ORDER.map((key) =>
        key === BLOG_POST ? blogPost && postSlide(blogPost) : FIXED_SLIDES.find((s) => s.key === key)
    ).filter(Boolean);

    const slides = [];
    for (let i = 0; i < Math.max(listings.length, extras.length); i++) {
        if (listings[i]) slides.push(listings[i]);
        if (extras[i]) slides.push(extras[i]);
    }
    return slides;
}

const Section = styled.section`
    background: ${(props) => props.theme.darkgradient};
    /* Small top padding: the heading row is already 2.5rem tall (the arrow buttons). */
    padding: 1rem 0 2rem;
    overflow: hidden;
    @media print {
        display: none;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    color: white;
`;

/* Heading and subheading share a line, wrapping only when there isn't room. */
const TitleLine = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.25rem 0.75rem;
`;

/* One step down from the site's h2 (1.75rem) and the slider's old mobile size. */
const Heading = styled.h2`
    color: white;
    margin: 0;
    font-size: 1.5rem;
    font-weight: 500;
    @media screen and (max-width: ${size.sm}px) {
        font-size: 1.25rem;
    }
`;

/* One step down from the site's paragraph size (0.9rem). */
const SubHeading = styled.p`
    color: ${(props) => props.theme.lightblue};
    margin: 0;
    font-size: 0.8rem;
`;

const Arrows = styled.div`
    display: flex;
    gap: 0.5rem;
    @media screen and (max-width: ${size.sm}px) {
        display: none;
    }
`;

const Arrow = styled.button`
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background: transparent;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: 0.3s;
    &:hover {
        background: ${(props) => props.theme.lightblue};
        border-color: ${(props) => props.theme.lightblue};
        color: ${(props) => props.theme.darkblue};
    }
`;

/*
 * The main slide takes most of the width; the neighbours only peek out
 * behind it by --peek on each side. The side slides are far wider than their
 * peek, so the stage clips them at the content edge - without that, how much
 * of them shows would depend on the viewport rather than on --peek.
 */
const Stage = styled.div`
    --peek: clamp(40px, 7vw, 110px);
    overflow: hidden;
    .swiper-container {
        overflow: visible;
        padding-bottom: 2.75rem;
    }
    .swiper-slide {
        width: calc(100% - 2 * var(--peek));
        transition-property: transform, filter;
        filter: brightness(0.5);
    }
    .swiper-slide-active {
        filter: none;
    }
    /* Side slides show only their photo; their text would just be clutter at the edges. */
    .swiper-slide:not(.swiper-slide-active) .slide-body {
        opacity: 0;
    }
    /* Only the front slide is a link; clicking a side slide brings it forward. */
    .swiper-slide:not(.swiper-slide-active) a {
        pointer-events: none;
    }
    .swiper-pagination {
        left: 0;
        width: 100%;
        white-space: nowrap;
    }
    .swiper-pagination-bullet {
        background: white;
        opacity: 0.4;
        transition: 0.3s;
    }
    .swiper-pagination-bullet-active {
        background: ${(props) => props.theme.lightblue};
        opacity: 1;
        width: 1.5rem;
        border-radius: 4px;
    }
`;

const Card = styled.div`
    position: relative;
    border-radius: 14px;
    overflow: hidden;
    aspect-ratio: 16 / 7;
    max-height: 60vh;
    width: 100%;
    background: ${(props) => props.theme.darkblue};
    @media screen and (max-width: ${size.sm}px) {
        aspect-ratio: 4 / 5;
    }
    & img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(12, 32, 59, 0) 35%, rgba(12, 32, 59, 0.92) 100%);
    }
`;

/*
 * Holds the slider's space while listings and posts load. The slider is the
 * first thing under the nav, so without this everything below it would jump
 * down when the slides arrive.
 */
const Placeholder = styled.div`
    width: calc(100% - 2 * var(--peek));
    margin: 0 auto 2.75rem;
    aspect-ratio: 16 / 7;
    max-height: 60vh;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.06);
    @media screen and (max-width: ${size.sm}px) {
        aspect-ratio: 4 / 5;
    }
`;

const CardBody = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    padding: 2rem;
    color: white;
    transition: opacity 0.4s;
    @media screen and (max-width: ${size.sm}px) {
        padding: 1.25rem;
    }
`;

const Tag = styled.span`
    display: inline-block;
    background: ${(props) => props.theme.lightblue};
    color: ${(props) => props.theme.darkblue};
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const Title = styled.h3`
    color: white;
    margin: 0.6rem 0 0.25rem;
    font-size: 1.75rem;
    font-weight: 500;
    /* Listing addresses are stored in mixed case; the site capitalises them everywhere. */
    text-transform: ${(props) => (props.$capitalize ? "capitalize" : "none")};
    @media screen and (max-width: ${size.sm}px) {
        font-size: 1.25rem;
    }
`;

const Subtitle = styled.p`
    margin: 0 0 1rem;
    max-width: 38rem;
    color: #d6ebf7;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const Cta = styled.span`
    display: inline-block;
    background: ${(props) => props.theme.lightblue};
    color: ${(props) => props.theme.darkblue};
    font-weight: 500;
    padding: 0.55rem 1.1rem;
    border-radius: 6px;
`;

function SlideCard({ slide }) {
    const card = (
        <Card>
            {slide.image && <img src={slide.image} alt="" loading="lazy" />}
            <CardBody className="slide-body">
                <Tag>{slide.tag}</Tag>
                <Title $capitalize={slide.isListing}>{slide.title}</Title>
                {slide.subtitle && <Subtitle>{slide.subtitle}</Subtitle>}
                <Cta>{slide.cta}</Cta>
            </CardBody>
        </Card>
    );

    if (slide.href) {
        return (
            <a href={slide.href} target="_blank" rel="noreferrer">
                {card}
            </a>
        );
    }
    return <Link to={slide.to}>{card}</Link>;
}

export default function LatestSlider({ properties }) {
    const [posts, setPosts] = useState(null);

    useEffect(() => {
        const axiosInstance = axios.create({ baseURL: process.env.REACT_APP_API_URL });
        axiosInstance
            .get("posts", { params: { published: true } })
            .then((res) => setPosts(Array.isArray(res.data) ? res.data : []))
            .catch(() => setPosts([]));
    }, []);

    const loading = posts === null || !properties.length;
    const slides = useMemo(() => (loading ? [] : buildSlides(properties, posts)), [loading, properties, posts]);

    if (!loading && slides.length < 3) return null;

    return (
        <Section aria-label="Latest listings, guides and news">
            <Container>
                <Header>
                    <TitleLine>
                        <Heading>Latest</Heading>
                        <SubHeading>New listings, guides and news</SubHeading>
                    </TitleLine>
                    <Arrows>
                        <Arrow className="latest-slider-prev" type="button" aria-label="Previous slide">
                            <ChevronLeft />
                        </Arrow>
                        <Arrow className="latest-slider-next" type="button" aria-label="Next slide">
                            <ChevronRight />
                        </Arrow>
                    </Arrows>
                </Header>
                <Stage>
                    {loading ? (
                        <Placeholder />
                    ) : (
                        <Swiper
                            effect="coverflow"
                            centeredSlides
                            slidesPerView="auto"
                            loop
                            loopedSlides={slides.length}
                            grabCursor
                            slideToClickedSlide
                            speed={600}
                            coverflowEffect={{ rotate: 30, stretch: 0, depth: 220, modifier: 1, slideShadows: false }}
                            autoplay={{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }}
                            navigation={{ prevEl: ".latest-slider-prev", nextEl: ".latest-slider-next" }}
                            pagination={{ clickable: true }}
                            keyboard={{ enabled: true, onlyInViewport: true }}
                        >
                            {slides.map((slide) => (
                                <SwiperSlide key={slide.key}>
                                    <SlideCard slide={slide} />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    )}
                </Stage>
            </Container>
        </Section>
    );
}
